import { ScopeButton } from '@monorepo/shared-lib';
import { Col, Form, Spin, Typography } from 'antd';
import { FormDatePicker } from 'components/Custom/datePicker';
import { LabelWrapper } from 'components/Custom/labels';
import { FormSelect } from 'components/Custom/selects';
import { MessageStates } from 'containers/MessageBox';
import dayjs, { Dayjs } from 'dayjs';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useCountryLabel } from 'hooks/useCountryLabel';
import { useDispatch, useSelector } from 'react-redux';
import { turnDrawerOff, turnMessageOn } from 'store/app/actions';
import { DataLogStatus, VatTaxReportType } from 'store/app/enums';
import { fetchCurrenciesRequest } from 'store/common/actions';
import { getCommonCurrencies, getCurrenciesLoaded } from 'store/common/selectors';
import { Mapping } from 'store/organisation/models';
import { generateReconciliationRequest } from 'store/reconciliation/actions';
import { getReconciliationLoading } from 'store/reconciliation/selectors';
import { getVatReturnLogs } from 'store/vat/selectors';
import styled from 'styled-components';
import { v4 as uuidv4 } from 'uuid';

const VatOverviewWrapper = styled.div`
  .overview-main {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
  }
`;

type FieldType = {
  mappingId: string;
  asAtDate: Dayjs;
  currency: string;
};

interface ReconciliationOverviewPageProps {
  organisation: any;
  selectedMaps: Mapping[];
}

export const ReconciliationOverviewPage: React.FC<
  ReconciliationOverviewPageProps
> = ({ organisation, selectedMaps }) => {
  const { t } = useTranslation();
  const { localizeCountry } = useCountryLabel();
  const dispatch = useDispatch();
  const loading = useSelector(getReconciliationLoading);
  const currencies = useSelector(getCommonCurrencies);
  const currenciesLoaded = useSelector(getCurrenciesLoaded);
  const vatReturnLogs = useSelector(getVatReturnLogs);
  const [form] = Form.useForm<FieldType>();
  const connectionId = organisation?.connectionId;
  const entityName = organisation?.friendlyName || organisation?.legalName;
  const retTypes = organisation?.returnTypesEdit || [];

  const mappingOptions = React.useMemo(() => {
    const markerForType = (type: VatTaxReportType | string | undefined) => {
      switch (type) {
        case VatTaxReportType.VAT_RETURN:
          return 'VAT';
        case VatTaxReportType.CASH_ACCOUNTING:
          return 'Cash';
        case VatTaxReportType.RECAP:
          return 'Statement';
        default:
          return 'Return';
      }
    };

    return (selectedMaps || [])
      .map((m) => {
        const taxReturn = retTypes.find((rt: any) => rt.id === m.returnTypeId);
        const isStatement = taxReturn?.type === VatTaxReportType.RECAP;
        if (isStatement) return null;
        const label = taxReturn
          ? `${markerForType(taxReturn.type)} · ${localizeCountry(taxReturn.countryName, taxReturn.countryName)} - ${
              taxReturn.name
            }`
          : m.mappingName || m.mappingId;
        return { label, value: m.mappingId };
      })
      .filter(Boolean) as Array<{ label: string; value: string }>;
  }, [selectedMaps, retTypes, localizeCountry]);

  React.useEffect(() => {
    if (currenciesLoaded) return;
    dispatch(fetchCurrenciesRequest());
  }, [currenciesLoaded, dispatch]);

  React.useEffect(() => {
    const c = organisation?.currency;
    const updates: Partial<FieldType> = {
      asAtDate: dayjs(),
    };
    if (c) updates.currency = c;
    form.setFieldsValue(updates);
  }, [organisation?.currency, form]);

  React.useEffect(() => {
    if (mappingOptions.length !== 1) return;
    const current = form.getFieldValue('mappingId');
    if (current) return;
    form.setFieldsValue({ mappingId: mappingOptions[0].value });
  }, [form, mappingOptions]);

  const handleSubmit = (values: FieldType) => {
    if (!connectionId) return;

    const mappingIdSet = new Set((selectedMaps || []).map((m) => m.mappingId));
    const selectedConnectionId = String(connectionId ?? '').trim();
    const hasSubmittedSubmission = (vatReturnLogs || []).some((vrl: any) => {
      const rawStatus = vrl?.status;
      if (rawStatus == null) return false;
      const s = String(rawStatus).trim();
      const isSubmitted =
        s === DataLogStatus.SUBMITTED ||
        s.toLowerCase() === 'submitted' ||
        s.toUpperCase() === 'SUBMITTED' ||
        (DataLogStatus as any)[s] === DataLogStatus.SUBMITTED;
      if (!isSubmitted) return false;
      const conn = String(
        vrl?.connectionId ?? vrl?.applicationConnectionID ?? ''
      ).trim();
      if (!selectedConnectionId || conn !== selectedConnectionId) return false;
      const logMappingId = String(vrl?.mappingId ?? '').trim();
      return !logMappingId || mappingIdSet.size === 0
        ? true
        : mappingIdSet.has(logMappingId);
    });

    if (!hasSubmittedSubmission) {
      dispatch(
        turnMessageOn({
          title: t('organisation.reconciliation-unavailable-title'),
          description: t('organisation.reconciliation-unavailable-description'),
          type: MessageStates.WARNING,
        })
      );
      return;
    }

    const toDate = values.asAtDate.format('YYYY-MM-DD');
    const mappingId = values.mappingId;
    const currency = values.currency;
    const reportId = uuidv4().replaceAll('-', '');

    dispatch(
      generateReconciliationRequest({
        connectionId,
        mappingId,
        toDate,
        currency,
        reportId,
      })
    );
    dispatch(turnDrawerOff());
  };

  return (
    <Spin spinning={loading} size="large">
      <Form layout="horizontal" form={form} onFinish={handleSubmit}>
        <VatOverviewWrapper>
          <div className="overview-main tax-submission-stretch">
            <div>
              <Col
                span={24}
                style={{
                  borderBottom: '1px solid var(--color-border, #dbeae3)',
                  marginBottom: 30,
                  paddingBottom: 15,
                }}
              >
                <Typography.Text style={{ fontSize: 14, fontWeight: 700 }}>
                  {entityName}
                </Typography.Text>
                <br />
                <Typography.Text style={{ fontSize: 16, fontWeight: 400 }}>
                  {mappingOptions.length
                    ? t('vat-report.select-return-mapping')
                    : t('vat-report.no-mappings-available')}
                </Typography.Text>
              </Col>
              <Col span={24}>
                <LabelWrapper>
                  <span className="label-text">
                    {t('vat-report.return-mapping')}
                  </span>
                </LabelWrapper>
                <Form.Item<FieldType>
                  name="mappingId"
                  rules={[
                    { required: true, message: t('common.required-field') },
                  ]}
                >
                  <FormSelect
                    width="100%"
                    height="45px"
                    placeholder={t('vat-report.select-return-or-mapping')}
                    optionFilterProp="children"
                    showSearch
                    bordercolor="var(--color-border, #dbeae3)"
                    bgcolor="var(--color-surface-raised, #FFFFFF)"
                    options={mappingOptions}
                    filterOption={(input: string, option: any) =>
                      option?.label?.toLowerCase().includes(input.toLowerCase())
                    }
                  />
                </Form.Item>
                <LabelWrapper>
                  <span className="label-text">
                    {t('vat-report.choose-as-at-date')}
                  </span>
                </LabelWrapper>
                <Form.Item<FieldType>
                  name="asAtDate"
                  rules={[
                    { required: true, message: t('common.required-field') },
                  ]}
                >
                  <FormDatePicker
                    format="DD/MM/YYYY"
                    width="100%"
                    allowClear={false}
                  />
                </Form.Item>
                <LabelWrapper>
                  <span className="label-text">
                    {t('vat-report.reporting-currency')}
                  </span>
                </LabelWrapper>
                <Form.Item<FieldType>
                  name="currency"
                  rules={[
                    { required: true, message: t('common.required-field') },
                  ]}
                >
                  <FormSelect
                    width="100%"
                    height="45px"
                    placeholder={t('vat-report.select-currency')}
                    optionFilterProp="children"
                    showSearch
                    bordercolor="var(--color-border, #dbeae3)"
                    bgcolor="var(--color-surface-raised, #FFFFFF)"
                    options={[...(currencies || [])].sort().map((c) => ({
                      label: c,
                      value: c,
                    }))}
                    filterOption={(input: string, option: any) =>
                      option?.label?.toLowerCase().includes(input.toLowerCase())
                    }
                  />
                </Form.Item>
              </Col>
            </div>
            <div>
              <Form.Item>
                <ScopeButton
                  width="100%"
                  type="primary"
                  htmlType="submit"
                  disabled={!mappingOptions.length}
                >
                  {t('vat-report.compile-transactions')}
                </ScopeButton>
              </Form.Item>
            </div>
          </div>
        </VatOverviewWrapper>
      </Form>
    </Spin>
  );
};
